import type { ReactNode } from 'react';
import { C } from '@ridgewill/brand';
import { adminApi, type NotificationSendDetail } from '../lib/api.js';
import { useAsync } from '../lib/useAsync.js';
import { exact } from '../lib/format.js';
import { isNotFound, reasonText, sendsPath } from '../lib/notifications.js';
import { RecordScreen } from '../components/Record.js';
import { Note, Panel, PanelBody, Table, Td, When } from '../components/ui.js';
import { Screen } from '../components/Screen.js';
import { detailRows } from './EventDetail.js';
import { DeliveryText, StatusPill } from '../components/NotificationStatus.js';

/**
 * One send, in full (org-admin#131): why it stopped, how many tries it took,
 * and what the provider said.
 *
 * Stacked panels rather than tabs, like an activity event: it is one thing that
 * happened, and the reason is the part somebody opened it for.
 *
 * NO CONTENT, and the screen says so. The service does not keep a message once
 * it is sent and the route never selects what it holds, so an empty space where
 * the body would be must not read as a failed load.
 */
export function SendDetail({ id, backTo }: { id: string; backTo: string }) {
  const { state } = useAsync(() => adminApi.notificationSend(id), [id]);

  if (state.status === 'loading') {
    return (
      <Screen>
        <Note>Loading…</Note>
      </Screen>
    );
  }
  if (state.status === 'failed') {
    return (
      <Screen>
        <a href={`#${backTo}`} style={back}>
          &larr; Sends
        </a>
        {isNotFound(state.message) ? (
          <Note>
            No send with this id could be read. It may not exist, your role may not allow reading
            sends, or the auth service may be older than the Notifications admin routes.
          </Note>
        ) : (
          <Note tone="danger">{state.message}</Note>
        )}
      </Screen>
    );
  }
  return <Send send={state.data} backTo={backTo} />;
}

function Send({ send, backTo }: { send: NotificationSendDetail; backTo: string }) {
  const reason = reasonText(send.reason);
  const detail = detailRows(send.deliveryDetail);
  const waiting = send.status === 'queued' || send.status === 'sending';

  return (
    <RecordScreen
      back={{ to: backTo, label: 'Sends' }}
      title={send.type}
      identifier={send.appSlug}
      badges={<StatusPill status={send.status} reason={send.reason} />}
      description={
        <>
          To{' '}
          <a href={`#${sendsPath(send.recipient)}`} style={{ color: C.text }}>
            {send.recipient}
          </a>
          , <When iso={send.createdAt} />
        </>
      }
    >
      <Panel title="Outcome">
        <PanelBody>
          <div style={s.facts}>
            <Fact label="Status">
              <StatusPill status={send.status} reason={send.reason} />
            </Fact>
            <Fact label="Delivery">
              <DeliveryText status={send.status} delivery={send.deliveryStatus} />
            </Fact>
            <Fact label="Queued">{time(send.createdAt)}</Fact>
            <Fact label="Decided">{waiting ? muted('Not yet') : time(send.decidedAt)}</Fact>
            <Fact label="Delivery updated">{time(send.deliveryUpdatedAt)}</Fact>
          </div>
          {reason ? <p style={s.reason}>{reason}</p> : null}
        </PanelBody>
      </Panel>

      <Panel title="Sending">
        <PanelBody>
          <div style={s.facts}>
            <Fact label="Attempts">{send.attempts}</Fact>
            {send.status === 'queued' && send.nextAttemptAt ? (
              <Fact label="Next attempt">{time(send.nextAttemptAt)}</Fact>
            ) : null}
            <Fact label="Category">{send.category}</Fact>
            <Fact label="Channel">{send.channel}</Fact>
            <Fact label="Provider message id">
              {send.providerMessageId ? <code style={s.code}>{send.providerMessageId}</code> : muted('None')}
            </Fact>
            <Fact label="Send id">
              <code style={s.code}>{send.id}</code>
            </Fact>
          </div>
          <Fact label="Last error">
            {send.lastError ? <span style={s.error}>{send.lastError}</span> : muted('None')}
          </Fact>
        </PanelBody>
      </Panel>

      {detail.length ? (
        <Panel title="Delivery detail">
          <Table columns={['Field', 'Value']}>
            {detail.map((row) => (
              <tr key={row.key}>
                <Td mono>{row.key}</Td>
                <Td>{row.value}</Td>
              </tr>
            ))}
          </Table>
        </Panel>
      ) : null}

      <Note>Message content isn&rsquo;t shown. The service doesn&rsquo;t keep it once a message is sent.</Note>
    </RecordScreen>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'grid', gap: '.2rem', minWidth: 0 }}>
      <span style={s.factLabel}>{label}</span>
      <span style={{ fontSize: '.88rem' }}>{children}</span>
    </div>
  );
}

/** The exact time, because a send's timeline is read to the second. */
function time(iso: string | null) {
  return iso ? <span title={exact(iso)}>{exact(iso)}</span> : muted('None');
}

function muted(text: string) {
  return <span style={{ color: C.muted }}>{text}</span>;
}

const back = {
  justifySelf: 'start' as const,
  fontSize: '.85rem',
  fontWeight: 500,
  color: C.muted,
  textDecoration: 'none',
};

const s = {
  facts: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
    gap: '.9rem 1.4rem',
  },
  factLabel: {
    fontSize: '.72rem',
    letterSpacing: '.06em',
    textTransform: 'uppercase' as const,
    color: C.muted,
    fontWeight: 640,
  },
  reason: { margin: 0, fontSize: '.88rem', lineHeight: 1.5 },
  code: { fontSize: '.8rem', wordBreak: 'break-all' as const },
  error: { fontFamily: 'ui-monospace, monospace', fontSize: '.8rem', whiteSpace: 'pre-wrap' as const },
};
